import React, { Suspense, lazy, useEffect, useState } from 'react';
import ApiHandler from './pages/ApiHandler.js';
import LoginPage from './pages/LoginPage.js';
import Start from './pages/Start.js';
import { SkeletonTable } from './components/ui/Skeleton.js';
import { AppShell } from './components/layout/AppShell.js';
import { ThemeProvider } from './theme/ThemeProvider.js';
import { ChalkFilters } from './theme/ChalkFilters.js';
import { ToastProvider } from './components/ui/Toast.js';
import { EventContext } from './contexts/EventContext.js';
import { useRole, vergissRolle } from './hooks/useRole.js';
import { naechsteVeranstaltung } from './features/events/datum.js';

export const UserContext = React.createContext(null);
// Mock data + role switcher are dev-only: they require the opt-in flag AND a
// non-production build, so they can never surface live — even if the flag is set
// by accident in a production environment.
//
// require statt import, und die Bedingung steht ausgeschrieben im Ausdruck:
// Nur so faltet webpack sie im Produktionsbuild zu `false` und lässt Mock-
// Daten und Umschalter ganz aus dem Bündel. Über eine Variable erkennt
// webpack das nicht — so wurden beide bisher mitgeliefert.
export const API =
  process.env.NODE_ENV !== 'production' && process.env.REACT_APP_MOCK_API === 'true'
    ? new (require('./mocks/MockApiHandler.js').default)()
    : new ApiHandler();
const MockRoleSwitcher =
  process.env.NODE_ENV !== 'production' && process.env.REACT_APP_MOCK_API === 'true'
    ? require('./mocks/MockRoleSwitcher.js').MockRoleSwitcher
    : null;

/* Seiten hinter der Startseite werden erst geladen, wenn man sie braucht.
   Vorher steckte alles in einem Bündel — wer sich nur anmeldet und auf die
   Startseite schaut, lud Einstellungen, Check-in und Personen mit.
   webpackPrefetch holt die Teile danach im Leerlauf nach, damit ein Klick
   auf „Anmeldungen" nicht erst auf das Netz wartet. Ihr CSS bleibt im
   Haupt-Stylesheet (stile.js). */
const SEITEN = {
  dashboard: () => import(/* webpackPrefetch: true */ './pages/Dashboard.js'),
  my: () => import(/* webpackPrefetch: true */ './pages/MyRegistration.js'),
  settings: () => import(/* webpackPrefetch: true */ './pages/Settings.js'),
  persons: () => import(/* webpackPrefetch: true */ './pages/Personen.js').then((m) => ({ default: m.Personen })),
  checkin: () => import(/* webpackPrefetch: true */ './pages/CheckIn.js'),
};
const Dashboard = lazy(SEITEN.dashboard);
const MyRegistration = lazy(SEITEN.my);
const Settings = lazy(SEITEN.settings);
const Personen = lazy(SEITEN.persons);
const CheckIn = lazy(SEITEN.checkin);

/* Seite und gewählte Veranstaltung überleben ein Neuladen, aber keinen
   neuen Besuch. Genau dafür ist sessionStorage da: er gilt pro Tab und ist
   beim nächsten Öffnen leer.

   Vorher fing die Seite immer bei der Startseite an und die Veranstaltung
   immer beim ersten Eintrag der Liste — wer im RoverMoot arbeitete und neu
   lud, landete wieder im Diözesanlager. */
const SESSION_PAGE = 'dpsg-page';
const SESSION_EVENT = 'dpsg-event';
/* Muss alle Seiten-IDs aus SideNav.js enthalten. Fehlt eine, wirft ein
   Neuladen auf dieser Seite die Person zurück auf Start — beim Umzug auf dev
   war genau das mit 'persons' passiert. */
/* 'kiosk' gibt es nicht mehr: Check-in und Kiosk sind eine Seite (CheckIn.js,
   Knopf „Vollbild“). Ein gemerktes 'kiosk' fällt damit auf 'start' zurück. */
const PAGES = ['start', 'dashboard', 'persons', 'my', 'settings', 'checkin'];

/* Wer auf einer nachgeladenen Seite neu lädt, soll nicht erst React, dann
   die Anmeldung und dann das Teilstück abwarten: Das Stück wird angefordert,
   sobald dieses Modul läuft — parallel zu allem anderen. Dieselbe
   import()-Zusage nimmt lazy() später einfach entgegen. */
try {
  const gemerkt = sessionStorage.getItem('dpsg-page');
  if (SEITEN[gemerkt]) SEITEN[gemerkt]();
} catch {
  /* ohne sessionStorage eben nicht */
}

function readSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeSession(key, value) {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
export function clearSessionState() {
  writeSession(SESSION_PAGE, null);
  writeSession(SESSION_EVENT, null);
}

function InnerApp({ onLogout }) {
  const { role } = useRole();
  // Nur bekannte Seitennamen zurückholen — ein alter oder verfälschter Wert
  // soll nicht zu einer leeren Ansicht führen.
  const [page, setPage] = useState(() => {
    const saved = readSession(SESSION_PAGE);
    return PAGES.includes(saved) ? saved : 'start';
  });
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    API.getEvents('').then((evs) => {
      setEvents(evs);
      // Die zuletzt gewählte Veranstaltung, sofern es sie noch gibt —
      // Rechte können sich geändert haben oder das Lager ist weg. Sonst die
      // laufende oder als nächste anfangende, nicht die je zuerst angelegte.
      const saved = readSession(SESSION_EVENT);
      setSelectedEvent(evs.find((ev) => ev.slug === saved) || naechsteVeranstaltung(evs) || evs[0] || null);
    });
  }, []);

  useEffect(() => {
    writeSession(SESSION_PAGE, page);
  }, [page]);
  useEffect(() => {
    if (selectedEvent?.slug) writeSession(SESSION_EVENT, selectedEvent.slug);
  }, [selectedEvent?.slug]);

  React.useEffect(() => {
    if (role === 'self' && page === 'dashboard') setPage('my');
  }, [role, page]);

  React.useEffect(() => {
    const handler = (e) => setPage(e.detail);
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, []);

  function renderPage() {
    if (page === 'start') return <Start onNavigate={setPage} />;
     // "Meine Anmeldung" gehört auch granted User (Issue #63): daneben
     // erreichen sie den Admin-Dashboard, brauchen aber die eigene
     // Registrierung als eigene Seite.
    if (page === 'my') return <MyRegistration />;
    // Reine Teilnehmende haben keine Teilnehmerliste — für sie führt alles
    // außer der Startseite auf die eigene Anmeldung.
    if (role === 'self') return <MyRegistration />;
    if (page === 'settings') return <Settings role={role} />;
    if (page === 'persons') return <Personen />;
    if (page === 'checkin') return <CheckIn />;
    return <Dashboard />;
   }

  return (
     <EventContext.Provider value={{ events, selectedEvent, setSelectedEvent }}>
       <AppShell page={page} onNavigate={setPage} onLogout={onLogout} role={role}>
         {/* Solange eine Seite nachlädt, steht das Lade-Gerippe da, das die
             Seiten selbst auch zeigen — kein Sprung, kein leerer Fleck. */}
         <Suspense fallback={<SkeletonTable />}>{renderPage()}</Suspense>
       </AppShell>
     </EventContext.Provider>
   );
}

function App() {
  const [loggedIn, setLoggedIn] = useState(true);
  const [loginMessage, setLoginMessage] = useState('');

  async function updateLoginState() {
    await API.getLoggedIn();
    setLoggedIn(API.loggedIn);
  }

  useEffect(() => {
    updateLoginState();
  }, [setLoggedIn, loggedIn]); // eslint-disable-line

  // Läuft die Sitzung während der Arbeit ab (24 h, Backend-Neustart), meldet
  // ApiHandler das hier; vorher blieb die Seite bis zum Reload bei „Nicht
  // berechtigt" stehen (#126).
  useEffect(() => {
    API.onSessionExpired = () => setLoggedIn(false);
    return () => {
      API.onSessionExpired = null;
    };
  }, []);

  function HandleLogin(user) {
    setLoginMessage('hide');
    vergissRolle();
    API.login(user.username, user.password).then((res) => {
      if (!res.success) setLoginMessage(res.data);
      else setLoggedIn(API.loggedIn);
    });
  }

  function HandleLogout() {
    setLoginMessage('');
    // Sonst landet die nächste Anmeldung auf der Seite und der Veranstaltung
    // der vorigen — auf einem gemeinsam genutzten Rechner unschön.
    clearSessionState();
    vergissRolle();
    API.logout().then(() => setLoggedIn(API.loggedIn));
  }

  return (
    <ThemeProvider>
      {/* Die Kreide-Filter müssen im Dokument liegen, bevor irgendein
          filter: url(#chalkf) darauf zeigt — deshalb ganz oben. */}
      <ChalkFilters />
      <ToastProvider>
        {/* Kein Router: Die Seiten wechseln über den Zustand `page`. Der
            BrowserRouter, der hier stand, wurde nirgends benutzt und brachte
            42 KB react-router ins Bündel. */}
        {loggedIn ? (
          <InnerApp onLogout={HandleLogout} />
        ) : (
          <LoginPage message={loginMessage} onLogin={HandleLogin} />
        )}
        {MockRoleSwitcher && <MockRoleSwitcher />}
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
